import { Box, Button, Flex, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import { Content } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { CompactValidationCard } from '@feature/validation/CompactValidationCard';
import { ValidationCard } from '@feature/validation/ValidationCard';
import { ValidationRejectModal } from '@feature/validation/ValidationRejectModal';
import { Validation, ValidationRequest, ValidationVariant } from '@feature/validation/ValidationTypes';

export interface ValidationActionProps {
  validation: Validation | null;

  onSubmit: (request: ValidationRequest) => void;
  isPending: boolean;

  variant?: ValidationVariant;
}

export function ValidationAction({ validation, onSubmit, isPending, variant }: ValidationActionProps) {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();
  const canValidate =
    currentRole === 'DISCIPLINE_ADMIN' ||
    currentRole === 'DEPARTMENT_ADMIN' ||
    currentRole === 'FACULTY_ADMIN' ||
    currentRole === 'UNIVERSITY_ADMIN';

  const onValidate = () => onSubmit({ note: '', status: 'VALIDATED' });
  const onReject = ({ note }: { note: string }) => {
    onSubmit({ note, status: 'REJECTED' });
    close();
  };

  const [opened, { open, close }] = useDisclosure(false);

  const isCompact = variant === 'compact';

  return (
    <Content type="full">
      <Flex
        justify="space-between"
        direction={isCompact ? 'column' : 'row'}
      >
        <Text
          fw="500"
          mb="2px"
        >
          {t('validation.current')}
        </Text>
        {opened && (
          <ValidationRejectModal
            onClose={close}
            isSubmitting={isPending}
            onSubmit={onReject}
          />
        )}
        {canValidate && (
          <Flex
            gap="4px"
            direction={isCompact ? 'column' : 'row'}
          >
            <Button
              size="xs"
              bg="green"
              onClick={onValidate}
              loading={isPending}
            >
              {t('validation.validate')}
            </Button>
            <Button
              size="xs"
              bg="red"
              onClick={open}
              loading={isPending}
            >
              {t('validation.reject')}
            </Button>
          </Flex>
        )}
      </Flex>
      {validation == null && (
        <Box maw={isCompact ? '130px' : undefined}>
          <Text
            size="sm"
            c="dimmed"
            fs="italic"
          >
            {t('validation.notValidated')}
          </Text>
        </Box>
      )}
      {validation != null &&
        (isCompact ? <CompactValidationCard validation={validation} /> : <ValidationCard validation={validation} />)}
    </Content>
  );
}
